import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { notificationService, PushSubscription } from '../services/notificationService';

interface NotificationContextType {
  // State
  isSupported: boolean;
  permission: NotificationPermission;
  isSubscribed: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  requestPermission: () => Promise<void>;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
  testNotification: () => Promise<void>;
  clearError: () => void;
  
  // Utility
  canSubscribe: boolean;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

interface NotificationProviderProps {
  children: ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isSupported = notificationService.isPushSupported();

  // Check if user can subscribe (supported + permission granted)
  const canSubscribe = isSupported && permission === 'granted';

  // Initialize notification state
  useEffect(() => {
    const initializeNotifications = async () => {
      if (!isSupported) return;

      try {
        // Check current permission
        const currentPermission = notificationService.getPermissionStatus();
        setPermission(currentPermission);

        // Try to retrieve existing subscription from backend
        if (currentPermission === 'granted') {
          const existingSubscription = await notificationService.retrieveExistingSubscription();
          setIsSubscribed(!!existingSubscription);
        } else {
          // Check if already subscribed locally
          const subscription = notificationService.getCurrentSubscription();
          setIsSubscribed(!!subscription);
        }
      } catch (error) {
        console.error('Error initializing notifications:', error);
        setError('Failed to initialize notifications');
      }
    };

    initializeNotifications();
  }, [isSupported]);

  // Request notification permission
  const requestPermission = async () => {
    if (!isSupported) {
      setError('Push notifications are not supported in this browser');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const newPermission = await notificationService.requestPermission();
      setPermission(newPermission);

      if (newPermission === 'granted') {
        // Wait a moment for the permission state to fully update
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Auto-subscribe after permission is granted
        await subscribe();
      } else if (newPermission === 'denied') {
        setError('Notification permission was denied. Please enable it in your browser settings.');
      }
    } catch (error) {
      console.error('Error requesting permission:', error);
      setError('Failed to request notification permission');
    } finally {
      setIsLoading(false);
    }
  };

  // Subscribe to push notifications
  const subscribe = async () => {
    // Double-check permission status before subscribing
    const currentPermission = notificationService.getPermissionStatus();
    if (currentPermission !== 'granted') {
      setError('Cannot subscribe: permission not granted');
      return;
    }

    if (!isSupported) {
      setError('Push notifications are not supported in this browser');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await notificationService.subscribe();
      setIsSubscribed(true);
    } catch (error) {
      console.error('Error subscribing to notifications:', error);
      setError('Failed to subscribe to notifications');
    } finally {
      setIsLoading(false);
    }
  };

  // Unsubscribe from push notifications
  const unsubscribe = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await notificationService.unsubscribe();
      setIsSubscribed(false);
    } catch (error) {
      console.error('Error unsubscribing from notifications:', error);
      setError('Failed to unsubscribe from notifications');
    } finally {
      setIsLoading(false);
    }
  };

  // Test notification
  const testNotification = async () => {
    if (!isSubscribed) {
      setError('Please subscribe to notifications first');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await notificationService.testNotification();
    } catch (error) {
      console.error('Error testing notification:', error);
      setError('Failed to send test notification');
    } finally {
      setIsLoading(false);
    }
  };

  // Clear error
  const clearError = () => {
    setError(null);
  };

  const value: NotificationContextType = {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    error,
    requestPermission,
    subscribe,
    unsubscribe,
    testNotification,
    clearError,
    canSubscribe,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

// Custom hook to use notification context
export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
